<template>
  <MainLayout>
    <!-- Menu Sub Navigation -->
    <MenuSubNavbar />
    <div class="px-6">
      <!-- Modifiers Content -->
      <PageHeader
        title="Grupos de Modificadores"
        subtitle="Administra grupos y opciones con precio delta"
      >
        <template #actions>
          <div class="flex items-center gap-3">
            <!-- View Mode Toggle -->
            <ViewModeToggle v-model:view-mode="viewMode" />
            
            <BaseButton
              @click="openModal()"
            >
              Nuevo Grupo
            </BaseButton>
          </div>
        </template>
      </PageHeader>
      
      <!-- Modifier Group List -->
      <ModifierGroupList
        :groups="groups"
        :loading="loading"
        :view-mode="viewMode"
        @edit="openEditModal"
        @toggle="toggleGroup"
        @delete="deleteGroup"
      />

      <!-- Modifier Group Modal -->
      <ModifierGroupModal
        :is-open="modalOpen"
        :group="editingGroup"
        :saving="saving"
        @close="closeModal"
        @save="saveGroup"
      />
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import type { ModifierGroup } from '@/types/menu';
import menuService from '@/services/menuService';
import { useToast } from '@/composables/useToast';
import MainLayout from '@/components/layout/MainLayout.vue';
import MenuSubNavbar from '@/components/menu/MenuSubNavbar.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import ModifierGroupList from '@/components/modifiers/ModifierGroupList.vue';
import ModifierGroupModal from '@/components/modifiers/ModifierGroupModal.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import ViewModeToggle from '@/components/common/ViewModeToggle.vue';

const { showSuccess, showError } = useToast();

// View mode state
const viewMode = ref<'list' | 'card'>('card');

const loading = ref(false);
const saving = ref(false);
const groups = ref<ModifierGroup[]>([]);
const modalOpen = ref(false);
const editingGroup = ref<ModifierGroup | null>(null);

const loadGroups = async () => {
  loading.value = true;
  try {
    groups.value = await menuService.getModifierGroups(true);
  } catch (error: any) {
    showError(error?.message || 'No se pudieron cargar los modificadores');
  } finally {
    loading.value = false;
    document.body.classList.toggle('menu-view-active', loading.value);
  }
};

onMounted(() => {
  document.body.classList.add('menu-view-active');
  loadGroups();
});

onUnmounted(() => {
  document.body.classList.remove('menu-view-active');
});

const openModal = () => {
  editingGroup.value = null;
  modalOpen.value = true;
};

const openEditModal = async (group: ModifierGroup) => {
  try {
    // Fetch individual data instead of using list data
    const freshGroupData = await menuService.getModifierGroup(group.id);
    editingGroup.value = freshGroupData;
    modalOpen.value = true;
  } catch (error: any) {
    showError(error?.message || 'No se pudo cargar el grupo de modificadores');
  }
};

const closeModal = () => {
  modalOpen.value = false;
  editingGroup.value = null;
};

const saveGroup = async (data: Omit<ModifierGroup, 'id'>) => {
  saving.value = true;
  try {
    if (editingGroup.value) {
      await menuService.updateModifierGroup(editingGroup.value.id, data);
      showSuccess('Grupo actualizado');
    } else {
      await menuService.createModifierGroup(data);
      showSuccess('Grupo creado');
    }

    await loadGroups();
    closeModal();
  } catch (error: any) {
    showError(error?.message || 'No se pudo guardar el grupo');
  } finally {
    saving.value = false;
  }
};

const deleteGroup = async (group: ModifierGroup) => {
  const confirmed = window.confirm(`¿Eliminar el grupo "${group.name}"?`);
  if (!confirmed) return;

  try {
    await menuService.deleteModifierGroup(group.id);
    showSuccess('Grupo eliminado');
    await loadGroups();
    
    if (editingGroup.value?.id === group.id) {
      closeModal();
    }
  } catch (error: any) {
    showError(error?.message || 'No se pudo eliminar el grupo');
  }
};

const toggleGroup = async (group: ModifierGroup) => {
  try {
    await menuService.updateModifierGroup(group.id, { 
      is_active: !(group.is_active ?? true) 
    });
    showSuccess('Estado actualizado');
    await loadGroups();
  } catch (error: any) {
    showError(error?.message || 'No se pudo actualizar el estado');
  }
};
</script>
